/**
 * MIQCalibrationStatus.js
 * 
 * Displays MIQ calibration status, timeline, and baseline/weekly brief UI
 * 
 * Features:
 * - 4-week calibration timeline
 * - Momentum bonus display
 * - Baseline report card (locked/unlocked)
 * - Weekly brief section
 */

import React, { useState, useMemo } from 'react';
import { Lock, Unlock, Calendar, TrendingUp, Award, ChevronRight, Info } from 'lucide-react';
import { brand } from '../../styles/brand';
import { track } from '../../utils/analytics';

const { colors, gradients, typography, radius, shadows, spacing } = brand;

// ═══════════════════════════════════════════════════════════════════════════════
// CALIBRATION TIMELINE COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════
export const CalibrationTimeline = ({ dayNumber, isGeniusTier }) => {
  // Define 4 weeks
  const weeks = [
    { label: 'Week 1', days: '1-7', start: 1, end: 7 },
    { label: 'Week 2', days: '8-14', start: 8, end: 14 },
    { label: 'Week 3', days: '15-21', start: 15, end: 21 },
    { label: 'Week 4', days: '22-30', start: 22, end: 30 }
  ];

  const getCurrentWeek = () => {
    if (dayNumber <= 7) return 0;
    if (dayNumber <= 14) return 1;
    if (dayNumber <= 21) return 2;
    return 3;
  };

  const currentWeek = getCurrentWeek();

  return (
    <div style={{
      background: colors.panel,
      border: `1px solid ${colors.stroke}`,
      borderRadius: radius.lg,
      padding: spacing.s4,
      marginBottom: spacing.s4
    }}>
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: spacing.s2,
        marginBottom: spacing.s3
      }}>
        <Calendar size={16} style={{ color: '#667eea' }} />
        <span style={{ 
          fontSize: typography.bodySmall.fontSize, 
          fontWeight: '600',
          color: colors.text
        }}>
          Weekly Calibration Timeline
        </span>
      </div>

      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(4, 1fr)', 
        gap: '4px'
      }}>
        {weeks.map((week, index) => {
          const isComplete = dayNumber > week.end;
          const isCurrent = index === currentWeek && dayNumber <= 30;
          const progress = isCurrent 
            ? Math.min(100, ((dayNumber - week.start + 1) / (week.end - week.start + 1)) * 100)
            : isComplete ? 100 : 0;

          return (
            <div key={week.label} style={{ textAlign: 'center' }}>
              {/* Progress bar */}
              <div style={{
                height: '6px',
                background: 'rgba(255,255,255,0.1)',
                borderRadius: '3px',
                overflow: 'hidden',
                marginBottom: '6px'
              }}>
                <div style={{
                  height: '100%',
                  width: `${progress}%`,
                  background: isCurrent 
                    ? 'linear-gradient(90deg, #667eea, #764ba2)' 
                    : isComplete 
                      ? '#38ef7d' 
                      : 'transparent',
                  transition: 'width 0.3s ease'
                }} />
              </div>
              
              {/* Week label */}
              <span style={{ 
                fontSize: '10px', 
                fontWeight: isCurrent ? '700' : '500',
                color: isCurrent ? '#667eea' : isComplete ? '#38ef7d' : colors.textSecondary
              }}>
                {week.label}
              </span>
              <br />
              <span style={{ 
                fontSize: '9px', 
                color: colors.textSecondary 
              }}>
                {week.days}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// MOMENTUM BONUS INDICATOR
// ═══════════════════════════════════════════════════════════════════════════════
export const MomentumBonusIndicator = ({ baseConfidence, showBonus }) => {
  const bonusAmount = showBonus ? 10 : 0;
  const displayConfidence = Math.min(99, baseConfidence + bonusAmount);

  if (!showBonus) return null;

  return (
    <div style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: '4px',
      background: 'rgba(56, 239, 125, 0.1)',
      border: '1px solid rgba(56, 239, 125, 0.3)',
      borderRadius: '12px',
      padding: '4px 10px',
      fontSize: '11px',
      color: '#38ef7d'
    }}>
      <TrendingUp size={12} />
      <span>+10 Momentum</span>
      <div style={{
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center'
      }}>
        <Info 
          size={12} 
          style={{ cursor: 'help' }}
          title="Momentum bonus applied during calibration"
        />
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// BASELINE REPORT CARD (Locked/Unlocked)
// ═══════════════════════════════════════════════════════════════════════════════
export const BaselineReportCard = ({ 
  dayNumber, 
  isGeniusTier, 
  onViewBaseline,
  onUpgrade 
}) => {
  const isUnlocked = dayNumber >= 30;
  const daysRemaining = Math.max(0, 30 - dayNumber);

  const handleClick = () => {
    if (isUnlocked && isGeniusTier) {
      track('miq_baseline_viewed');
      onViewBaseline?.();
    } else if (!isGeniusTier) {
      track('miq_upgrade_clicked_from_miq', { context: 'baseline_card' });
      onUpgrade?.();
    }
  };

  return (
    <div 
      onClick={handleClick}
      style={{
        background: isUnlocked 
          ? 'linear-gradient(135deg, rgba(102, 126, 234, 0.15) 0%, rgba(118, 75, 162, 0.15) 100%)'
          : colors.panel,
        border: `1px solid ${isUnlocked ? 'rgba(102, 126, 234, 0.4)' : colors.stroke}`,
        borderRadius: radius.lg,
        padding: spacing.s4,
        marginBottom: spacing.s4,
        cursor: (isUnlocked && isGeniusTier) || !isGeniusTier ? 'pointer' : 'default',
        opacity: isUnlocked || isGeniusTier ? 1 : 0.8,
        transition: 'all 0.2s ease'
      }}
    >
      <div style={{ 
        display: 'flex', 
        alignItems: 'flex-start', 
        justifyContent: 'space-between'
      }}>
        <div style={{ flex: 1 }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: spacing.s2,
            marginBottom: spacing.s2
          }}>
            {isUnlocked ? (
              <Unlock size={18} style={{ color: '#667eea' }} />
            ) : (
              <Lock size={18} style={{ color: colors.textSecondary }} />
            )}
            <span style={{ 
              fontSize: typography.body.fontSize, 
              fontWeight: '600',
              color: colors.text
            }}>
              MIQ Baseline Report {!isUnlocked && '(Unlocks Day 30)'}
            </span>
          </div>
          
          <p style={{ 
            margin: 0, 
            fontSize: typography.bodySmall.fontSize, 
            color: colors.textSecondary,
            lineHeight: 1.4
          }}>
            {isUnlocked 
              ? 'Your metabolic baseline has been established from 30 days of patterns.'
              : 'Your baseline is established after 30 days of patterns.'}
          </p>
          
          {!isUnlocked && (
            <div style={{ 
              marginTop: spacing.s3,
              fontSize: typography.bodySmall.fontSize 
            }}>
              {isGeniusTier ? (
                <span style={{ color: '#667eea' }}>
                  Available in {daysRemaining} days
                </span>
              ) : (
                <span style={{ 
                  color: '#FFD700',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}>
                  <Award size={14} />
                  Upgrade to Genius Plaite
                </span>
              )}
            </div>
          )}
        </div>
        
        {isUnlocked && isGeniusTier && (
          <ChevronRight size={20} style={{ color: '#667eea' }} />
        )}
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// WEEKLY BRIEF SECTION
// ═══════════════════════════════════════════════════════════════════════════════
export const WeeklyBriefSection = ({ 
  dayNumber, 
  isGeniusTier, 
  weeklyBrief,
  onViewBrief,
  onUpgrade 
}) => {
  const isUnlocked = dayNumber >= 30;

  if (!isUnlocked) {
    return (
      <div style={{
        background: colors.panel,
        border: `1px solid ${colors.stroke}`,
        borderRadius: radius.lg,
        padding: spacing.s4,
        marginBottom: spacing.s4,
        opacity: 0.7
      }}>
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: spacing.s2,
          marginBottom: spacing.s2
        }}>
          <Lock size={16} style={{ color: colors.textSecondary }} />
          <span style={{ 
            fontSize: typography.body.fontSize, 
            fontWeight: '600',
            color: colors.textSecondary
          }}>
            Weekly MIQ Intelligence Brief
          </span>
        </div>
        <p style={{ 
          margin: 0, 
          fontSize: typography.bodySmall.fontSize, 
          color: colors.textSecondary
        }}>
          Weekly MIQ Intelligence Briefs begin after baseline completion.
        </p>
      </div>
    );
  }

  if (!isGeniusTier) {
    return (
      <div 
        onClick={() => {
          track('miq_upgrade_clicked_from_miq', { context: 'weekly_brief' });
          onUpgrade?.();
        }}
        style={{
          background: colors.panel,
          border: `1px solid ${colors.stroke}`,
          borderRadius: radius.lg,
          padding: spacing.s4,
          marginBottom: spacing.s4,
          cursor: 'pointer'
        }}
      >
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: spacing.s2,
          marginBottom: spacing.s2
        }}>
          <Lock size={16} style={{ color: '#FFD700' }} />
          <span style={{ 
            fontSize: typography.body.fontSize, 
            fontWeight: '600',
            color: colors.text
          }}>
            Weekly MIQ Intelligence Brief
          </span>
          <span style={{
            fontSize: '10px',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            padding: '2px 8px',
            borderRadius: '10px',
            fontWeight: '600'
          }}>
            GENIUS
          </span>
        </div>
        <p style={{ 
          margin: 0, 
          fontSize: typography.bodySmall.fontSize, 
          color: '#FFD700'
        }}>
          Upgrade to Genius Plaite to unlock MIQ Smart Coach and weekly intelligence briefs
        </p>
      </div>
    );
  }

  // Genius tier with unlocked brief
  return (
    <div 
      onClick={() => {
        track('miq_weekly_brief_viewed');
        onViewBrief?.();
      }}
      style={{
        background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
        border: '1px solid rgba(102, 126, 234, 0.3)',
        borderRadius: radius.lg,
        padding: spacing.s4,
        marginBottom: spacing.s4,
        cursor: 'pointer'
      }}
    >
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between'
      }}>
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: spacing.s2
        }}>
          <Award size={18} style={{ color: '#667eea' }} />
          <span style={{ 
            fontSize: typography.body.fontSize, 
            fontWeight: '600',
            color: colors.text
          }}>
            Weekly MIQ Intelligence Brief
          </span>
        </div>
        <ChevronRight size={20} style={{ color: '#667eea' }} />
      </div>
      {weeklyBrief?.generated_at && (
        <p style={{ 
          margin: `${spacing.s2} 0 0 0`, 
          fontSize: '11px', 
          color: colors.textSecondary
        }}>
          Last updated: {new Date(weeklyBrief.generated_at).toLocaleDateString()}
        </p>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// STATUS TAG COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════
export const CalibrationStatusTag = ({ dayNumber }) => {
  const isCalibrating = dayNumber < 30;
  
  return (
    <div style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: '6px',
      background: isCalibrating 
        ? 'linear-gradient(135deg, rgba(102, 126, 234, 0.2) 0%, rgba(118, 75, 162, 0.2) 100%)'
        : 'linear-gradient(135deg, rgba(56, 239, 125, 0.2) 0%, rgba(0, 184, 148, 0.2) 100%)',
      border: `1px solid ${isCalibrating ? 'rgba(102, 126, 234, 0.4)' : 'rgba(56, 239, 125, 0.4)'}`,
      borderRadius: '20px',
      padding: '6px 12px',
      fontSize: '12px',
      fontWeight: '600',
      color: isCalibrating ? '#667eea' : '#38ef7d'
    }}>
      <span style={{
        width: '8px',
        height: '8px',
        borderRadius: '50%',
        background: isCalibrating ? '#667eea' : '#38ef7d',
        animation: isCalibrating ? 'pulse 2s infinite' : 'none'
      }} />
      {isCalibrating ? 'Calibration in Progress' : 'Baseline Ready'}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN CALIBRATION STATUS COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════
const MIQCalibrationStatus = ({
  dayNumber = 1,
  baseConfidence = 0,
  isGeniusTier = false,
  weeklyBrief = null,
  onViewBaseline,
  onViewWeeklyBrief,
  onUpgrade
}) => {
  // Calculate momentum bonus eligibility
  const showMomentumBonus = baseConfidence >= 25 && dayNumber < 30;
  const displayConfidence = showMomentumBonus 
    ? Math.min(99, baseConfidence + 10) 
    : baseConfidence;

  return (
    <div>
      {/* Header with Status Tag */}
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        marginBottom: spacing.s4
      }}>
        <CalibrationStatusTag dayNumber={dayNumber} />
        {showMomentumBonus && (
          <MomentumBonusIndicator 
            baseConfidence={baseConfidence} 
            showBonus={showMomentumBonus} 
          />
        )}
      </div>

      {/* Day Counter */}
      <div style={{
        background: colors.panel,
        border: `1px solid ${colors.stroke}`,
        borderRadius: radius.md,
        padding: spacing.s3,
        marginBottom: spacing.s4,
        textAlign: 'center'
      }}>
        <span style={{ 
          fontSize: typography.bodySmall.fontSize, 
          color: colors.textSecondary 
        }}>
          MIQ Calibration Progress
        </span>
        <div style={{ 
          fontSize: '24px', 
          fontWeight: '700',
          color: colors.text,
          marginTop: '4px'
        }}>
          Day {Math.min(dayNumber, 30)} of 30
        </div>
      </div>

      {/* Timeline */}
      <CalibrationTimeline dayNumber={dayNumber} isGeniusTier={isGeniusTier} />

      {/* Baseline Report Card */}
      <BaselineReportCard 
        dayNumber={dayNumber}
        isGeniusTier={isGeniusTier}
        onViewBaseline={onViewBaseline}
        onUpgrade={onUpgrade}
      />

      {/* Weekly Brief Section */}
      <WeeklyBriefSection
        dayNumber={dayNumber}
        isGeniusTier={isGeniusTier}
        weeklyBrief={weeklyBrief}
        onViewBrief={onViewWeeklyBrief}
        onUpgrade={onUpgrade}
      />

      {/* Disclaimer */}
      <p style={{
        fontSize: '10px',
        color: colors.textSecondary,
        textAlign: 'center',
        margin: `${spacing.s4} 0 0 0`,
        fontStyle: 'italic'
      }}>
        MIQ provides personalized pattern insights and does not diagnose or treat medical conditions.
      </p>
    </div>
  );
};

export default MIQCalibrationStatus;
